'use client';

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { Circle } from 'lucide-react';

import { cn } from '../../../lib/utils';

/** The shadcn/ui RadioGroup, on Radix `@radix-ui/react-radio-group`.
 *
 *  The unconfigured parts retain shadcn's standard radio presentation. The `variant`, `size` and
 *  `nowrap` extensions are the app's segmented presentation: the same Radix roles and keyboard model,
 *  restyled as connected choices without duplicating selection or roving-focus behaviour. */
const radioGroupVariants = cva('grid gap-3', {
  variants: {
    variant: {
      default: 'inline-flex max-w-full gap-0.5 rounded-lg border border-border bg-control p-0.5',
      line: 'inline-flex max-w-full gap-5 border-b border-border',
    },
    nowrap: {
      true: '',
      false: '',
    },
    // The OUTER box of the track presentation, which is the height a row of controls has to agree on:
    // `default` matches the default field beside it, `sm` is the compact group. The option fills what the
    // border and padding leave, so no part of the scale is restated on an item or at a call site.
    size: {
      sm: '',
      default: '',
    },
  },
  compoundVariants: [
    // Connected choices retain one line; Segmented enables the native horizontal scroller by default.
    {
      variant: ['default', 'line'],
      className: 'flex-nowrap',
    },
    {
      variant: ['default', 'line'],
      nowrap: true,
      className: 'overflow-x-auto overflow-y-hidden overscroll-x-contain',
    },
    // 36 / 32 px for a pointer, and the 44 px `--touch-target` floor for a finger. The floor belongs to the
    // track, not to the option: raising the option instead would add the border and padding on top of it
    // and draw a 50 px box around a 44 px target. `line` is an underline strip with no box, so it takes
    // neither step.
    { variant: 'default', size: 'sm', className: 'h-8 pointer-coarse:min-h-[var(--touch-target)]' },
    { variant: 'default', size: 'default', className: 'h-9 pointer-coarse:min-h-[var(--touch-target)]' },
  ],
});

const radioGroupItemVariants = cva(
  'outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      appearance: {
        radio: [
          'aspect-square size-4 shrink-0 rounded-full border border-input text-primary shadow-card',
          'transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
          'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
        ],
        segmented: [
          'segmented__option inline-flex shrink-0 enabled:cursor-pointer items-center gap-1.5 text-small transition-colors',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          '[&_[data-slot=radio-group-indicator]]:hidden',
        ],
      },
      variant: {
        default: '',
        line: '',
      },
      size: {
        sm: '',
        default: '',
      },
    },
    compoundVariants: [
      {
        appearance: 'segmented',
        variant: 'default',
        className: [
          'rounded-md text-muted-foreground enabled:data-[state=unchecked]:hover:bg-accent/25 enabled:data-[state=unchecked]:hover:text-accent-foreground',
          'data-[state=checked]:bg-sidebar-accent data-[state=checked]:text-sidebar-accent-foreground data-[state=checked]:shadow-card',
        ],
      },
      {
        appearance: 'segmented',
        variant: 'line',
        // The compact line tab treatment: text with a 2px underline, no wash and no primary colour. Only
        // the underline says which tab is open. An overlay header enlarges it into drawer tabs through its
        // `dialog-tabs` slot (primitives.css, "Overlay header tabs").
        className: [
          'h-auto px-0 pb-2.75 pt-0 text-ui',
          // The underline strip is no box, so it has no outer track height to take the coarse step: the tab
          // itself keeps the touch minimum it has always had, and the `dialog-tabs` slot adds only padding
          // and typography on top of it. The boxed `default` track above owns the floor instead.
          'pointer-coarse:min-h-[var(--touch-target)]',
          '-mb-px border-b-2 border-transparent text-muted-foreground',
          'enabled:hover:text-foreground',
          'data-[state=checked]:border-foreground data-[state=checked]:text-foreground',
        ],
      },
      {
        appearance: 'segmented',
        variant: 'default',
        size: 'sm',
        // Padding only: the track owns the height and the option stretches to fill it (flex default
        // `align-items: stretch`), which is what keeps md 36 and sm 32 px intact as ONE box.
        className: 'px-2.5',
      },
      {
        appearance: 'segmented',
        variant: 'default',
        size: 'default',
        className: 'px-3',
      },
    ],
    defaultVariants: {
      appearance: 'radio',
    },
  },
);

type RadioGroupProps = React.ComponentProps<typeof RadioGroupPrimitive.Root> &
  VariantProps<typeof radioGroupVariants>;

function RadioGroup({ className = '', variant, size, nowrap = false, ...props }: RadioGroupProps) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-group"
      data-variant={variant ?? undefined}
      className={cn(radioGroupVariants({ variant, size, nowrap }), className)}
      {...props}
    />
  );
}

type RadioGroupItemProps = React.ComponentProps<typeof RadioGroupPrimitive.Item> &
  VariantProps<typeof radioGroupItemVariants>;

function RadioGroupItem({
  className = '',
  appearance,
  variant,
  size,
  children,
  ...props
}: RadioGroupItemProps) {
  return (
    <RadioGroupPrimitive.Item
      data-slot="radio-group-item"
      data-variant={variant ?? undefined}
      className={cn(radioGroupItemVariants({ appearance, variant, size }), className)}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <Circle className="absolute left-1/2 top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 fill-primary" />
      </RadioGroupPrimitive.Indicator>
      {children}
    </RadioGroupPrimitive.Item>
  );
}

export { RadioGroup, RadioGroupItem };
